// Authentication mode for the API - either via URL query parameter or Authorization header
export type AuthMode = "query" | "header";

// Parameters required to make an address cleansing API call
export interface CleanseParams {
  baseUrl: string; // Base URL of the API service (e.g. https://api.ideal-postcodes.co.uk)
  apiKey: string; // API key for authentication
  authMode: AuthMode; // How to send the API key (query param or header)
  body: Record<string, any>; // The address data to be cleansed
}

// Response structure from the address cleansing API
export interface CleanseResult {
  confidence?: number; // Confidence score of the cleansing result (0-1)
  result?: any; // The cleansed address data
  meta?: any; // Additional metadata from the API
  message?: string; // any message from the API (success or error)
  code?: number; // API response code
}

// Response structure from the key availability API
export interface KeyAvailabilityResult {
  available: boolean; // Whether the API key is currently useable
  message?: string; // Any message from the API
  code?: number; // API response code
}

// Read the response as JSON, falling back to raw text if parsing fails.
// Returns nulls for an unreadable body rather than throwing.
async function readBody(
  res: Response
): Promise<{ data: any; rawText?: string }> {
  try {
    return { data: await res.json() };
  } catch {
    try {
      return { data: null, rawText: await res.text() };
    } catch {
      return { data: null };
    }
  }
}

/**
 * Main function to cleanse an address using the Ideal Postcodes API
 * Takes raw address data and returns a cleansed, standardized version
 */
export async function cleanseAddress(
  params: CleanseParams
): Promise<CleanseResult> {
  // Destructure the input parameters for easier access
  const { baseUrl, apiKey, authMode, body } = params;

  // Build the API endpoint URL - Ideal Postcodes Address Cleanse endpoint is versioned
  // Remove any trailing slash from baseUrl to avoid double slashes
  const url = new URL("/v1/cleanse/addresses", baseUrl.replace(/\/$/, ""));

  // Add API key to URL query parameters if using 'query' auth mode
  if (authMode === "query") {
    url.searchParams.set("api_key", apiKey);
  }

  // Add tag parameter for tracking and usage analytics
  url.searchParams.set("tags", "browsercleanse");

  // Make the HTTP POST request to the API
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // Add Authorization header if using 'header' auth mode, otherwise add nothing
      ...(authMode === "header" ? { Authorization: `Bearer ${apiKey}` } : {}),
    },
    // Send the address data as JSON in the request body
    body: JSON.stringify(body),
  });

  const { data, rawText } = await readBody(res);

  // Check if the HTTP request was successful (status 200-299)
  if (!res.ok) {
    // Create a meaningful error message from available sources
    const msg = data?.message || rawText || `${res.status} ${res.statusText}`;

    // Special handling for credit exhaustion (402 Payment Required)
    if (res.status === 402) {
      const creditError = new Error(`Insufficient API credits. ${msg}`);
      (creditError as any).code = "INSUFFICIENT_CREDITS";
      (creditError as any).status = 402;
      throw creditError;
    }

    throw new Error(`${msg} (${url.toString()})`);
  }

  // Ideal Postcodes APIs commonly include code/message in response body
  // Extract and normalize the response data into our standard format
  return {
    // Confidence can be at root level or nested in result object
    confidence: data?.confidence ?? data?.result?.confidence,
    // Result data - use nested result if available, otherwise use entire data object
    result: data?.result ?? data,
    // Pass through any metadata from the API
    meta: data?.meta,
    // Pass through any messages from the API
    message: data?.message,
    // Pass through any status codes from the API
    code: data?.code,
  };
}

/**
 * Parameters required to make a UPRN lookup API call
 */
export interface UprnLookupParams {
  baseUrl: string; // Base URL of the API service
  apiKey: string; // API key for authentication
  uprn: string; // UPRN to lookup
}

/**
 * Function to lookup an address by UPRN using the Ideal Postcodes API
 * Takes a UPRN and returns the full address details
 */
export async function lookupByUprn(
  params: UprnLookupParams
): Promise<CleanseResult> {
  const { baseUrl, apiKey, uprn } = params;

  // Build the API endpoint URL for UPRN lookup
  const url = new URL("/v1/addresses", baseUrl.replace(/\/$/, ""));
  url.searchParams.set("api_key", apiKey);
  url.searchParams.set("uprn", uprn);

  // Make the HTTP GET request to the API
  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  const { data, rawText } = await readBody(res);

  // Check if the HTTP request was successful
  if (!res.ok) {
    const msg = data?.message || rawText || `${res.status} ${res.statusText}`;

    // Special handling for credit exhaustion (402 Payment Required)
    if (res.status === 402) {
      const creditError = new Error(`Insufficient API credits. ${msg}`);
      (creditError as any).code = "INSUFFICIENT_CREDITS";
      (creditError as any).status = 402;
      throw creditError;
    }

    throw new Error(`${msg} (${url.toString()})`);
  }

  // Extract the result - UPRN lookup returns hits array within result
  const hits = data?.result?.hits || [];
  
  // If no results found, throw an error instead of returning empty data
  if (hits.length === 0) {
    throw new Error(`No address found for UPRN: ${uprn}`);
  }
  
  const firstHit = hits[0];

  return {
    result: firstHit,
    message: data?.message,
    code: data?.code,
  };
}

/**
 * Function to validate an API key using the Ideal Postcodes key availability endpoint
 * Returns whether the key is available and useable
 */
export async function validateApiKey(
  baseUrl: string,
  apiKey: string
): Promise<KeyAvailabilityResult> {
  // Build the API endpoint URL for key availability check
  // Remove any trailing slash from baseUrl to avoid double slashes
  const url = new URL(`/v1/keys/${apiKey}`, baseUrl.replace(/\/$/, ""));

  try {
    // Make the HTTP GET request to the key availability endpoint
    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const { data, rawText } = await readBody(res);

    // Handle different response scenarios
    if (res.status === 404) {
      // Key not found - invalid API key
      return {
        available: false,
        message: "Invalid API key",
        code: 404,
      };
    }

    if (!res.ok) {
      // Other HTTP errors
      const msg = data?.message || rawText || `${res.status} ${res.statusText}`;
      return {
        available: false,
        message: msg,
        code: res.status,
      };
    }

    // Success response - extract availability from result.available field
    const isValid = data?.result?.available ?? false;
    return {
      available: isValid,
      message: data?.message || "API key validated successfully",
      code: data?.code || 200,
    };
  } catch (error) {
    // Network or other errors
    return {
      available: false,
      message:
        error instanceof Error ? error.message : "Failed to validate API key",
      code: 0,
    };
  }
}
